import type { TaskType } from '@shared/config'
import type { ExtraFieldName, ExtraFieldValue, ExtraFieldsValues } from '@shared/model'
import type { TasksKanbanStore } from './interfaces'

import { TaskPriority } from '@entities/task'

export const actions = {
  setTasks (this: TasksKanbanStore, payload: TasksKanbanStore['tasks']): void {
    this.tasks = payload
  },

  setAgents (this: TasksKanbanStore, payload: TasksKanbanStore['agents']): void {
    this.agents = payload
  },

  setCustomer (this: TasksKanbanStore, payload: TasksKanbanStore['customer']): void {
    this.customer = payload
  },

  setRelatedTo (this: TasksKanbanStore, payload: TasksKanbanStore['relatedTo']): void {
    this.relatedTo = payload
  },

  setTaskType (this: TasksKanbanStore, payload: TasksKanbanStore['taskType']): void {
    this.taskType = payload
  },

  setTeam (this: TasksKanbanStore, payload: TasksKanbanStore['team']): void {
    this.team = payload
  },

  setStage (this: TasksKanbanStore, payload: TasksKanbanStore['stage']): void {
    this.stage = payload
  },

  setPriority (this: TasksKanbanStore, payload: TasksKanbanStore['priority']): void {
    this.priority = payload
  },

  setDeadline (this: TasksKanbanStore, payload: TasksKanbanStore['deadline']): void {
    this.deadline = payload
  },

  setCreatedAt (this: TasksKanbanStore, payload: TasksKanbanStore['createdAt']): void {
    this.createdAt = payload
  },

  setUpdatedAt (this: TasksKanbanStore, payload: TasksKanbanStore['updatedAt']): void {
    this.updatedAt = payload
  },

  setAssignee (this: TasksKanbanStore, payload: TasksKanbanStore['assignee']): void {
    this.assignee = payload
  },

  setCreator (this: TasksKanbanStore, payload: TasksKanbanStore['creator']): void {
    this.creator = payload
  },

  setWaitingReason (this: TasksKanbanStore, payload: TasksKanbanStore['waitingReason']): void {
    this.waitingReason = payload
  },

  setResolution (this: TasksKanbanStore, payload: TasksKanbanStore['resolution']): void {
    this.resolution = payload
  },

  setCustomerOutcome (this: TasksKanbanStore, payload: TasksKanbanStore['customerOutcome']): void {
    this.customerOutcome = payload
  },

  setCancelReason (this: TasksKanbanStore, payload: TasksKanbanStore['cancelReason']): void {
    this.cancelReason = payload
  },

  clearTransitionFields (this: TasksKanbanStore): void {
    this.waitingReason = null
    this.resolution = null
    this.customerOutcome = null
    this.cancelReason = null
  },

  setSubmitted (this: TasksKanbanStore, payload: TasksKanbanStore['submitted']): void {
    this.submitted = payload
  },

  setComment (this: TasksKanbanStore, payload: TasksKanbanStore['comment']): void {
    this.comment = payload
  },

  setLogs (this: TasksKanbanStore, payload: TasksKanbanStore['logs']): void {
    this.logs = payload
  },

  appendLogs (this: TasksKanbanStore, payload: TasksKanbanStore['logs']): void {
    this.logs = [...this.logs, ...payload]
  },

  setLogsPagination (this: TasksKanbanStore, payload: TasksKanbanStore['logsPagination']): void {
    this.logsPagination = payload
  },

  isTaskType (this: TasksKanbanStore, payload: TaskType): boolean {
    return this.taskType === payload
  },

  setExtraField (this: TasksKanbanStore, payload: { name: ExtraFieldName, value: ExtraFieldValue }): void {
    const taskType = this.taskType
    if (!taskType) return

    this.extraFieldsByTaskType = {
      ...this.extraFieldsByTaskType,
      [taskType]: {
        ...this.extraFieldsByTaskType[taskType],
        [payload.name]: payload.value,
      },
    }
  },

  setExtraFieldsForTaskType (this: TasksKanbanStore, payload: { taskType: TaskType, values: ExtraFieldsValues }): void {
    this.extraFieldsByTaskType = {
      ...this.extraFieldsByTaskType,
      [payload.taskType]: { ...payload.values },
    }
  },

  clearExtraFieldsForTaskType (this: TasksKanbanStore, payload: TaskType): void {
    this.extraFieldsByTaskType = Object.fromEntries(
      Object.entries(this.extraFieldsByTaskType).filter(([key]) => key !== payload),
    )
  },

  clearAllExtraFields (this: TasksKanbanStore): void {
    this.extraFieldsByTaskType = {}
  },

  clearCreationFields (this: TasksKanbanStore): void {
    this.taskType = null
    this.team = null
    this.stage = null
    this.priority = TaskPriority.NoPriority
    this.deadline = null
    this.assignee = null
    this.customer = null
    this.relatedTo = null
    this.comment = null
    this.submitted = false
    this.extraFieldsByTaskType = {}
  },

  clearTaskDetail (this: TasksKanbanStore): void {
    this.taskType = null
    this.team = null
    this.stage = null
    this.priority = TaskPriority.NoPriority
    this.deadline = null
    this.createdAt = null
    this.updatedAt = null
    this.assignee = null
    this.creator = null
    this.customer = null
    this.relatedTo = null
    this.waitingReason = null
    this.resolution = null
    this.customerOutcome = null
    this.cancelReason = null
    this.comment = null
    this.submitted = false
    this.extraFieldsByTaskType = {}
    this.logs = []
    this.logsPagination = {
      per_page: 25,
      next_cursor: null,
      next_page_url: null,
      prev_cursor: null,
      prev_page_url: null,
    }
  },
}
